import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  CLINIC_ROLES,
  BILLING_CODES_99458,
  REPORT_SEVERITY,
  RPM_DEVICE_OPTIONS,
  TOAST_SEVERITY,
  manufacturerMappings,
  CLINIC_NAMES,
  CUSTOM_NOTES,
  EXTRACTABLE_ASSOCIATED_NOTES,
  EXTRACTABLE_MANUFACTURERS,
} from "static/constants";

import { isValidArray } from "utils/array";
import formatDate, { changeLocalDateTimeToUTC } from "utils/formatDate";
import {
  checkHasMultipleRpmManufacturers,
  checkIsRpmManufacturer,
} from "utils/rpmDevice.util";
import { checkIsInvalidValue } from "utils/string";
import { showErrorToast, showSuccessToast, showToast } from "utils/toast";

import {
  uploadInOfficeFile,
  uploadReport,
  extractReportPdfData,
} from "app/services/manualUpload/manualUpload";

import { fetchManufacturers } from "app/services/dashboard/manufacturerService";
import { updatePatientBasicInformation } from "app/services/transmission/patient";
import { useGetDeviceListQuery } from "app/store/devices/deviceApi";
import {
  setExistingPatientFieldValues,
  setInOfficeFile,
  setInOfficeFileName,
  setInOfficeUploadPatientDetails,
  setIsExistingPatient,
  setIsInOfficeUpload,
  setIsSubmitClicked,
  setIsSubmitting,
  setIsUploadingInOfficeFile,
  setPatientAdditionalManufacturers,
  setPatientManufacturers,
  setPdfFileNames,
  setPdfPreviewUrl,
  setReportTemplates,
  setSelectedBillingCodes,
  setSelectedPatient,
  setShowAddNewDeviceModal,
  setShowCustomNotes,
  setShowInOfficeUploadPatientFoundDetails,
  setShowReportTemplateUpdateAlert,
  setShowSwitchUserTypeModal,
  setSimilarPatientsFromInOfficeFile,
  setTransmissionType,
  setUploadReportId,
  setShowCrossFacilityPatientWarningModal,
  setSelectedCrossFacilityPatient,
  setShowMissingBillingCodeTemplateModal,
  setMissingTemplateBillingCodes,
  setExtractedPdfData,
} from "app/store/manualUpload/slice/manualUploadSlice";
import { setManufacturers } from "app/store/manufacturers/slice/manufacturers.slice";
import useAuthorization from "hooks/useAuthorization";
import {
  checkValidBillingCodes,
  checkIsIDTFBilling,
} from "utils/billingCode.util";
import { getFormattedDate } from "utils/date";
import { manufacturers } from "static/manufacturers";
import { findPatientRecordsByName } from "app/services/patient/patient";
import { isEmpty } from "lodash";
import { resetRpmPatientValues } from "app/store/rpmPatients/slice/rpmPatientsSlice";
import { formatReportNotes } from "utils/reportTemplate.util";
import { fetchReportTemplate } from "app/services/report_templates/reportNoteTemplateService";
import { filterNonUCHAccounts } from "utils/account.util";
import {
  checkIsCurrentUserOfClinic,
  getClinicUserFacility,
} from "utils/currentUser.util";

const useManualUpload = () => {
  const { data: devices } = useGetDeviceListQuery();

  const {
    isSubmitting,
    inOfficeFile,
    isSubmitClicked,
    selectedPatient,
    showCustomNotes,
    transmissionType,
    isInOfficeUpload,
    isExistingPatient,
    patientManufacturers,
    showSwitchUserTypeModal,
    showCrossFacilityPatientWarningModal,
    selectedCrossFacilityPatient,
    showAddNewDeviceModal,
    inOfficeUploadPatientDetails,
    showInOfficeUploadPatientFoundDetails,
    existingPatientFieldValues,
    similarPatientsFromInOfficeFile,
    inOfficeUploadPatientInformationForManualUpload,
    reportTemplates,
    showReportTemplateUpdateAlert,
    patientInfoToUpdate,
    showMissingBillingCodeTemplateModal,
    extractedPdfData,
  } = useSelector((state) => state.manualUpload);

  const { currentUser } = useSelector((state) => state.auth);
  const isClinicUser = CLINIC_ROLES.includes(currentUser?.role);
  const { accountsMapper } = useSelector((state) => state.account);

  const {
    checkHasAccountAccess,
    accessedAccountMappings,
    checkHasManufacturerAccess,
  } = useAuthorization();

  const [isUCHPatient, setIsUCHPatient] = useState(false);

  const dispatch = useDispatch();

  const fieldsModel = {
    name: "",
    account: "",
    reportAccount: "",
    manufacturer: "",
    accountFolder: "",
    manufacturerFolder: "",
    mrn: "",
    file: "",
    dob: null,
    doi: null,
    device: "",
    deviceSerialNumber: "",
    report: null,
    evoke_id: "",
    lastName: "",
    modelName: "",
    firstName: "",
    modelNumber: "",
    newPatient: false,
    report_date: null,
    clinician_notes: "",
    billingDiagnosisCodes: [],
    physicians: [],
    assignedPhysician: "",
    patient_serial_number: "",
    report_billing_date: null,
    isRpmDevice: false,
    transmissionType: "remote",
    custom_note: "",
    severity: REPORT_SEVERITY[0], // None
    additionalManufacturer: [],
    rpmDevices: {},
    leadInformation: {},
    batteryInformation: {},
    otherDeviceInformation: {},
    anticoagStatus: "unknown",
    anticoag: "",
    refusesOAC: false,
    reportNotes: [],
    sendToReviewTab: false,
    laaoDevice: false,
    nonBillable: false,
  };

  const [formErrors, setFormErrors] = useState([]);
  const [fields, setFields] = useState(fieldsModel);
  const [selectedReport, setSelectedReport] = useState(null);

  const isIDTFBilling = checkIsIDTFBilling({
    account: accountsMapper[fields.account],
    patient: selectedPatient,
    isNewPatient: !isExistingPatient,
  });
  const [fetchingManufacturers, setFetchingManufacturers] = useState(false);
  const [generalInformationUpdate, setGeneralInformationUpdate] =
    useState(false);
  const [isFetchingRpmPatient, setIsFetchingRpmPatient] = useState(false);
  const [isInitComplete, setIsInitComplete] = useState(false);
  const [
    showSimilarPatientsFromInOfficeFile,
    setShowSimilarPatientsFromInOfficeFile,
  ] = useState(false);
  const [isFetchingReportTemplates, setIsFetchingReportTemplates] =
    useState(false);
  const [pdfFiles, setPdfFiles] = useState([]);
  const [pendingAssociatedNotes, setPendingAssociatedNotes] = useState(
    new Set()
  );
  const [pendingExtractionResults, setPendingExtractionResults] = useState([]);
  const [extractionAttemptFailed, setExtractionAttemptFailed] = useState(false);

  const onFilesSelected = (files) => {
    setPdfFiles(files || []);
    if (!files?.length) {
      setPendingExtractionResults([]);
      setPendingAssociatedNotes(new Set());
      setExtractionAttemptFailed(false);
    }
  };

  useEffect(() => {
    setExtractionAttemptFailed(false);
  }, [pdfFiles, fields.manufacturer, reportTemplates]);

  useEffect(() => {
    setPendingExtractionResults([]);
    dispatch(setExtractedPdfData({}));
  }, [fields.manufacturer]);

  const isExtractableManufacturer = EXTRACTABLE_MANUFACTURERS.includes(
    fields.manufacturer
  );

  const findLiveReportNote = (note) =>
    (fields.reportNotes || []).find(
      (fieldNote) => fieldNote.note?.toLowerCase() === note.note?.toLowerCase()
    );

  const extractableNoteStatuses = (reportTemplates || [])
    .filter((note) =>
      EXTRACTABLE_ASSOCIATED_NOTES.includes(note?.associated_note)
    )
    .map((note) => {
      const fieldsMatch = findLiveReportNote(note);
      const content = fieldsMatch?.content || [];
      const isEdited = content[content.length - 1]?.isDefault === false;

      const knownResult = pendingExtractionResults.find(
        (entry) => entry.associatedNote === note.associated_note
      );
      const isResolved = Boolean(
        knownResult &&
        content.some((item) => item.description === knownResult.description)
      );

      return { associatedNote: note.associated_note, isEdited, isResolved };
    });

  const hasExtractableNotes = extractableNoteStatuses.length > 0;
  const showExtractButton = isExtractableManufacturer && hasExtractableNotes;

  const hasUnresolvedExtractableNotes = extractableNoteStatuses.some(
    (status) => !status.isResolved
  );

  const canExtractReportNotes =
    showExtractButton &&
    pdfFiles.length > 0 &&
    pendingAssociatedNotes.size === 0 &&
    !extractionAttemptFailed &&
    hasUnresolvedExtractableNotes;

  const extractReportNotesTooltip =
    pendingAssociatedNotes.size > 0
      ? "Extracting report data from PDF…"
      : !pdfFiles.length
        ? "Upload a PDF to extract report data"
        : extractionAttemptFailed
          ? "No report data could be extracted from the uploaded PDF"
          : !hasUnresolvedExtractableNotes
            ? "Report data has already been extracted"
            : "Extract report data from uploaded PDF";

  const { manufacturers: manufacturerList } = useSelector(
    (state) => state.manufacturers
  );

  const {
    isTransferredFromRpm,
    selectedRpmPatient,
    rpmReportUrl,
    rpmPatientBilling,
  } = useSelector((state) => state.rpmPatient);

  const onInitComponent = useCallback(() => {
    resetForm();
    resetPdfFiles();
    validateFields(fields);
    setIsInitComplete(true);
  }, []);

  useEffect(() => {
    onInitComponent();
  }, [onInitComponent]);

  useEffect(() => {
    if (isInitComplete && isTransferredFromRpm && selectedRpmPatient) {
      handleReportFromRpm();
    }
  }, [isTransferredFromRpm, selectedRpmPatient, isInitComplete]);

  // Cleanup for reports transferred from RPM
  useEffect(() => {
    return () => {
      dispatch(resetRpmPatientValues());
      dispatch(setSimilarPatientsFromInOfficeFile([]));
    };
  }, []);

  useEffect(() => {
    if (similarPatientsFromInOfficeFile?.length > 0) {
      setShowSimilarPatientsFromInOfficeFile(true);
      dispatch(setShowInOfficeUploadPatientFoundDetails(false));
    }
  }, [similarPatientsFromInOfficeFile]);

  const handleReportFromRpm = async () => {
    try {
      setIsFetchingRpmPatient(true);
      const { name, id } = selectedRpmPatient;
      const patientRecordsByName = await findPatientRecordsByName(name, false);
      const patientList = patientRecordsByName?.patients;
      const rpmPatient = patientList?.filter((patient) => patient._id === id);

      if (rpmPatient.length) {
        return handleExistingPatientSelect(rpmPatient[0]);
      }
      showErrorToast("RPM Patient not found.");
    } catch (error) {
      showErrorToast(error.message);
    } finally {
      setIsFetchingRpmPatient(false);
    }
  };

  const [nonRequiredFields, setNonRequiredFields] = useState([
    "mrn",
    "modelName",
    "rpmDevices",
    "physicians",
    "assignedPhysician",
    "evoke_id",
    "anticoag",
    "modelNumber",
    "custom_note",
    "anticoagStatus",
    "refusesOAC",
    "laaoDevice",
    "clinician_notes",
    "deviceSerialNumber",
    "report_billing_date",
    "patient_serial_number",
    "billingDiagnosisCodes",
    "additionalManufacturer",
    "batteryRemainingPercentage",
    "reportNotes",
    "facility",
  ]);

  useEffect(() => {
    const nonRequiredFieldsSet = new Set(nonRequiredFields);
    if (isExistingPatient) {
      nonRequiredFieldsSet.add("doi");
      nonRequiredFieldsSet.add("modelName");
      nonRequiredFieldsSet.add("modelNumber");
      nonRequiredFieldsSet.add("rpmDevices");
      if (fields.isRpmDevice) {
        nonRequiredFieldsSet.add("device");
        nonRequiredFieldsSet.add("billingDiagnosisCodes");
        nonRequiredFieldsSet.delete("rpmDevices");
      } else {
        nonRequiredFieldsSet.delete("device");
        nonRequiredFieldsSet.delete("billingDiagnosisCodes");
      }
    } else {
      if (fields.isRpmDevice) {
        nonRequiredFieldsSet.add("modelName");
        nonRequiredFieldsSet.add("modelNumber");
        nonRequiredFieldsSet.add("doi");
        nonRequiredFieldsSet.delete("rpmDevices");
        nonRequiredFieldsSet.add("billingDiagnosisCodes");
      } else {
        nonRequiredFieldsSet.add("rpmDevices");
        nonRequiredFieldsSet.delete("billingDiagnosisCodes");
        nonRequiredFieldsSet.delete("doi");
        nonRequiredFieldsSet.delete("modelName");
        nonRequiredFieldsSet.delete("modelNumber");
      }

      if (showCustomNotes) {
        nonRequiredFieldsSet.delete("custom_note");
      } else {
        nonRequiredFieldsSet.add("custom_note");
      }

      if (fields.anticoagStatus === "yes") {
        nonRequiredFieldsSet.delete("anticoag");
      } else {
        nonRequiredFieldsSet.add("anticoag");
      }
    }

    if (fields.nonBillable) {
      nonRequiredFieldsSet.add("billingDiagnosisCode");
    } else {
      nonRequiredFieldsSet.delete("billingDiagnosisCode");
    }

    setNonRequiredFields(Array.from(nonRequiredFieldsSet));
  }, [
    showCustomNotes,
    isExistingPatient,
    fields.isRpmDevice,
    fields.nonBillable,
  ]);

  const resetForm = () => {
    setFields(fieldsModel);
    setGeneralInformationUpdate(false);
    resetInOfficeUpload();
    dispatch(setSelectedBillingCodes([]));
    dispatch(setSelectedPatient(null));
    dispatch(setShowCustomNotes(false));
    dispatch(setPatientManufacturers([]));
    dispatch(setPatientAdditionalManufacturers([]));
    dispatch(setTransmissionType("remote"));
    dispatch(setReportTemplates([]));
    dispatch(setMissingTemplateBillingCodes([]));
    dispatch(setShowMissingBillingCodeTemplateModal(false));
  };
  const resetPdfFiles = () => {
    dispatch(setPdfFileNames([]));
    dispatch(setPdfPreviewUrl(null));
    dispatch(setExtractedPdfData({}));
    setSelectedReport(null);
    onFilesSelected([]);
  };

  const onUpdate = (updates) => {
    const generalInformationKeys = ["dob"];
    const fieldsCopy = Object.assign({}, existingPatientFieldValues || fields);

    for (const [key, value] of Object.entries(updates)) {
      fieldsCopy[key] = value;

      if (key === "account") {
        const match = accessedAccountMappings?.find(
          (account) => account.name === value
        );
        fieldsCopy.accountFolder = match.folder || "";
        fieldsCopy.physicians = [];
      }

      if (key === "manufacturer") {
        const matchedManufacturer = manufacturerMappings.find(
          (manufacturer) => manufacturer.name === value
        );

        fieldsCopy.manufacturerFolder = matchedManufacturer?.folder || "";
        fieldsCopy.additionalManufacturer = [];
        fieldsCopy.rpmDevices = {};

        !isTransferredFromRpm && dispatch(setSelectedBillingCodes([]));
        const isRpmDevice = checkIsRpmManufacturer(value);
        fieldsCopy.isRpmDevice = isRpmDevice;

        let reportTemplates = [];
        if (isRpmDevice) {
          fieldsCopy.device = value;
          reportTemplates = [
            { note: CUSTOM_NOTES, placeholders: [], content: [] },
          ];
        }

        const filteredAdditionalManufacturers = patientManufacturers.filter(
          (option) =>
            option.rpmDevice && option.name !== fieldsCopy.manufacturer
        );

        dispatch(setReportTemplates(reportTemplates));
        dispatch(
          setPatientAdditionalManufacturers(filteredAdditionalManufacturers)
        );
      }

      if (key === "additionalManufacturer") {
        if (fieldsCopy.isRpmDevice) {
          const manufacturers = [
            ...new Set([
              fieldsCopy.manufacturer,
              ...(fieldsCopy.additionalManufacturer || []),
            ]),
          ];

          const hasMultipleRpmManufacturers =
            checkHasMultipleRpmManufacturers(manufacturers);

          fieldsCopy.device = hasMultipleRpmManufacturers
            ? "Evoke RPM"
            : fieldsCopy.manufacturer;
        }
      }

      if (key === "modelName") {
        fieldsCopy.device = value;
      }

      if (
        key === "firstName" ||
        key === "lastName" ||
        key === "device" ||
        key === "report_date"
      ) {
        const {
          device = "",
          lastName = "",
          firstName = "",
          report_date = "",
        } = fieldsCopy;

        fieldsCopy.name = `${lastName} ${firstName}`;
        fieldsCopy.file = `${lastName}, ${firstName} ${report_date} ${device}`;
      }

      if (key === "report") {
        setSelectedReport(value);
      }

      if (key === "anticoagStatus") {
        const nonRequiredFieldsSet = new Set(nonRequiredFields);
        value === "yes"
          ? nonRequiredFieldsSet.delete("anticoag")
          : nonRequiredFieldsSet.add("anticoag");
        setNonRequiredFields(Array.from(nonRequiredFieldsSet));
      }

      if (key === "nonBillable") {
        fieldsCopy.report_billing_date = null;
      }

      if (isExistingPatient && generalInformationKeys.includes(key)) {
        setGeneralInformationUpdate(true);
      }
    }

    setFields(fieldsCopy);
    validateFields(fieldsCopy);
    dispatch(setExistingPatientFieldValues(null));

    // fetch report templates if "account", "modelName", "billingDiagnosisCodes" have been updated
    if (
      Object.keys(updates).some((key) =>
        [
          "account",
          "modelName",
          "billingDiagnosisCodes",
          "nonBillable",
        ].includes(key)
      )
    ) {
      handleReportTemplates(fieldsCopy);
    }
  };

  const handleReportTemplates = async (values) => {
    try {
      const {
        account,
        device,
        billingDiagnosisCodes,
        nonBillable,
        reportAccount,
      } = values;
      const deviceExists = Boolean(device);
      const accountExists = Boolean(account);
      const billingDiagnosisCodesExists = Boolean(
        billingDiagnosisCodes?.length
      );

      if (
        !deviceExists ||
        !accountExists ||
        (!nonBillable && !billingDiagnosisCodesExists)
      ) {
        return;
      }

      setIsFetchingReportTemplates(true);
      const billingCodes = billingDiagnosisCodes.map(
        (billingDiagnosisCode) => billingDiagnosisCode.billingCode
      );

      dispatch(setReportTemplates([]));

      const payload = {
        device,
        account: reportAccount || account,
        billingCodes,
      };

      if (nonBillable) {
        payload.nonBillable = true;
      }

      const reportTemplateResponse = await fetchReportTemplate(payload);

      const noteList = [
        ...(reportTemplateResponse?.flatMap((template) => template.sections) ??
          []),
      ];

      const reportNoteList = formatReportNotes(
        noteList,
        true,
        extractedPdfData
      );
      dispatch(setReportTemplates(reportNoteList));

      const templatesBillingCodes =
        reportTemplateResponse?.map((template) => template.billingCode) ?? [];

      const missingTemplateBillingCodes = billingCodes.filter(
        (code) => !templatesBillingCodes.includes(code)
      );

      if (
        missingTemplateBillingCodes.length > 0 &&
        CLINIC_ROLES.includes(currentUser?.role)
      ) {
        dispatch(setMissingTemplateBillingCodes(missingTemplateBillingCodes));
        dispatch(setShowMissingBillingCodeTemplateModal(true));
      }
    } catch (error) {
      showErrorToast(error.message);
    } finally {
      setIsFetchingReportTemplates(false);
    }
  };

  const applyExtractionResults = (entries) => {
    if (!entries.length) return;
    setPendingExtractionResults((prev) => {
      const merged = new Map(
        prev.map((entry) => [entry.associatedNote, entry])
      );
      entries.forEach((entry) => merged.set(entry.associatedNote, entry));
      return [...merged.values()];
    });
  };

  const handleExtractReportPdfData = async () => {
    if (!showExtractButton || !pdfFiles.length) return;

    const eligibleAssociatedNotes = new Set(
      extractableNoteStatuses.map((status) => status.associatedNote)
    );
    const editedAssociatedNotes = new Set(
      extractableNoteStatuses
        .filter((status) => status.isEdited)
        .map((status) => status.associatedNote)
    );

    const reusableResults = pendingExtractionResults.filter((entry) =>
      eligibleAssociatedNotes.has(entry.associatedNote)
    );
    const reusableAssociatedNotes = new Set(
      reusableResults.map((entry) => entry.associatedNote)
    );

    const neededAssociatedNotes = [...eligibleAssociatedNotes].filter(
      (associatedNote) =>
        !reusableAssociatedNotes.has(associatedNote) &&
        (editedAssociatedNotes.has(associatedNote) ||
          !extractedPdfData?.[associatedNote])
    );

    if (reusableResults.length) {
      applyExtractionResults(
        reusableResults.map((entry) => ({
          ...entry,
          mode: editedAssociatedNotes.has(entry.associatedNote)
            ? "append"
            : "fill",
        }))
      );
    }

    if (!neededAssociatedNotes.length) {
      if (!reusableResults.length) {
        showToast({
          message:
            "There is no new report data to extract from the uploaded PDF.",
          type: TOAST_SEVERITY.INFO,
        });
      }
      return;
    }

    setPendingAssociatedNotes(
      (prev) => new Set([...prev, ...neededAssociatedNotes])
    );

    try {
      const result = await extractReportPdfData(
        pdfFiles,
        fields.manufacturer,
        neededAssociatedNotes
      );

      if (result && Object.keys(result).length) {
        dispatch(setExtractedPdfData({ ...extractedPdfData, ...result }));

        applyExtractionResults(
          Object.entries(result).map(([associatedNote, description]) => ({
            associatedNote,
            description,
            mode: editedAssociatedNotes.has(associatedNote) ? "append" : "fill",
          }))
        );
        showSuccessToast("Report data extracted from PDF.");
      } else {
        setExtractionAttemptFailed(true);
        showToast({
          message: "Report notes could not be extracted from the uploaded PDF.",
          type: TOAST_SEVERITY.WARNING,
        });
      }
    } catch (error) {
      showErrorToast(error.message);
    } finally {
      setPendingAssociatedNotes((prev) => {
        const next = new Set(prev);
        neededAssociatedNotes.forEach((note) => next.delete(note));
        return next;
      });
    }
  };

  const validateFields = useCallback(
    (fields) => {
      const errors = [];

      for (const key in fields) {
        const value = fields[key];

        let isInvalidValue =
          checkIsInvalidValue(value) && nonRequiredFields.indexOf(key) === -1;

        if (key === "billingDiagnosisCodes") {
          const billingDiagnosisCodeList = fields.billingDiagnosisCodes;
          if (fields.nonBillable) {
            isInvalidValue = false;
          } else if (isValidArray(billingDiagnosisCodeList)) {
            const hasMissingDiagnosis = billingDiagnosisCodeList?.some(
              (code) => !code.diagnosisCode
            );

            let hasInvalidCodes = false;
            if (!fields.isRpmDevice && !isInOfficeUpload) {
              const selectedBillingCodes = [
                ...new Set(
                  billingDiagnosisCodeList?.map((code) => code?.billingCode)
                ),
              ];

              hasInvalidCodes = checkValidBillingCodes(
                selectedBillingCodes,
                isIDTFBilling
              );
            }

            isInvalidValue =
              !fields.isRpmDevice && (hasMissingDiagnosis || hasInvalidCodes);
          }
        }

        if (isInvalidValue) {
          errors.push(key);
        } else {
          const index = errors.findIndex((error) => error === key);
          errors.splice(index, 0);
        }
      }

      setFormErrors(errors);
      return errors;
    },
    [nonRequiredFields, isInOfficeUpload, isIDTFBilling]
  );

  useEffect(() => {
    if (isSubmitClicked) {
      validateFields(fields);
    }
  }, [fields, isSubmitClicked, validateFields]);

  const formatPayload = (payload) => {
    const fieldsCopy = Object.assign({}, payload);

    fieldsCopy.report_date = fieldsCopy.report_date
      ? changeLocalDateTimeToUTC(fieldsCopy.report_date, "YYYY-MM-DDTHH:mm:ss")
      : null;
    fieldsCopy.report_billing_date =
      formatDate(fieldsCopy.report_billing_date, "YYYY-MM-DD") || "";
    fieldsCopy.dob = formatDate(fieldsCopy.dob, "MM/DD/YYYY");
    fieldsCopy.doi = formatDate(fieldsCopy.doi, "YYYY-MM-DD") || "";
    fieldsCopy.transmissionType = transmissionType;
    fieldsCopy.newPatient = !isExistingPatient;
    fieldsCopy.physicians = fieldsCopy.physicians || [];

    fieldsCopy.modelName = fieldsCopy?.device || fieldsCopy.modelName || "";
    fieldsCopy.modelNumber =
      fieldsCopy?.deviceSerialNumber || fieldsCopy?.modelNumber || "";

    const isRpmDevice = checkIsRpmManufacturer(fieldsCopy?.manufacturer);

    if (!isRpmDevice) {
      fieldsCopy.deviceSerialNumber =
        fieldsCopy?.deviceSerialNumber || fieldsCopy?.modelNumber;
    }

    if (isRpmDevice) {
      fieldsCopy.modelName = "";
      fieldsCopy.modelNumber = "";
    }

    if (!showCustomNotes) {
      delete fieldsCopy.severity;
    }

    if (isCrossFacilityClinicUpload(fieldsCopy.account)) {
      fieldsCopy.account = fieldsCopy.reportAccount;
      fieldsCopy.facility = getClinicUserFacility();
    }

    const billingDiagnosisCodes = [];
    fieldsCopy.billingDiagnosisCodes.forEach((item) => {
      const is99458 = BILLING_CODES_99458.includes(item.billingCode);
      const code = is99458 ? "99458" : item.billingCode;

      const existingItem = billingDiagnosisCodes.find(
        ({ billingCode }) => billingCode === code
      );

      if (existingItem) {
        existingItem.count += item.count;
      } else {
        billingDiagnosisCodes.push({ ...item, billingCode: code });
      }
    });
    fieldsCopy.billingDiagnosisCodes = billingDiagnosisCodes;

    delete fieldsCopy.imei;
    delete fieldsCopy.evoke_id;
    delete fieldsCopy.reportAccount;

    return fieldsCopy;
  };

  const saveUpload = async (e) => {
    try {
      e.preventDefault();

      const hasUCHAccount =
        selectedReport &&
        isExistingPatient &&
        fields.account === CLINIC_NAMES.UCH;
      setIsUCHPatient(hasUCHAccount);

      dispatch(setIsSubmitting(true));
      dispatch(setIsSubmitClicked(true));
      if (!selectedReport) {
        return showErrorToast("Select a report to upload");
      }

      if (
        isCrossFacilityClinicUpload(fields.account) &&
        !fields.assignedPhysician
      ) {
        return showErrorToast("Assigned physician is required.");
      }

      !fields.report && (fields.report = selectedReport);
      const formattedPayload = formatPayload(fields);
      const errors = validateFields(formattedPayload);

      if (!errors.length && !hasUCHAccount) {
        generalInformationUpdate && (await updateGeneralInformation());
        const result = await uploadReport(formattedPayload);

        if (result) {
          if (result.type === "Error") {
            showErrorToast(result.message);
            handleExistingPatientSelect(result.data);
            dispatch(setIsExistingPatient(!isExistingPatient));
          } else {
            showSuccessToast(result.message);
            dispatch(setUploadReportId(result.data));
            resetForm();
            resetPdfFiles();
          }
          dispatch(setIsSubmitClicked(false));
          if (isTransferredFromRpm) {
            dispatch(resetRpmPatientValues());
          }
        }
      }
    } catch (error) {
      showErrorToast(error.message);
    } finally {
      dispatch(setIsSubmitting(false));
    }
  };

  const updateGeneralInformation = async () => {
    const { serial_number } = selectedPatient;
    const { dob } = fields; // Extract general information keys to be updated
    const payload = {
      dob,
    };

    await updatePatientBasicInformation(serial_number, payload);
  };

  const isError = useCallback(
    (field) => {
      const index = formErrors.findIndex((error) => error === field);
      return index > -1;
    },
    [formErrors]
  );

  const getRpmManufacturerForRpmTransmission = (rpmDevices) => {
    if (isEmpty(rpmDevices)) {
      return null;
    }

    const activeRpmDeviceKeys = Object.entries(rpmDevices)
      ?.filter(([, value]) => value.active)
      ?.map(([key]) => key);

    // Prioritize BP Cuff
    const hasBpCuffActive = activeRpmDeviceKeys.includes("body_trace_bp");
    const activeRpmDeviceKey = hasBpCuffActive
      ? "body_trace_bp"
      : activeRpmDeviceKeys[0];

    return (
      RPM_DEVICE_OPTIONS.find(
        (rpmDevice) => rpmDevice.key === activeRpmDeviceKey
      )?.value || null
    );
  };

  const handleExistingPatientSelect = (newValue) => {
    if (!newValue) {
      return;
    }
    dispatch(
      setSelectedPatient({
        id: newValue._id,
        name: newValue?.name || "",
        device:
          newValue.device || newValue.deviceInformation?.modelName || null,
        ...newValue,
      })
    );

    let patientManufacturer = isTransferredFromRpm
      ? getRpmManufacturerForRpmTransmission(newValue.rpm_devices)
      : newValue.manufacturer;

    const isRpmDevice = patientManufacturer
      ? checkIsRpmManufacturer(patientManufacturer)
      : "";
    const matchedAccount = accessedAccountMappings?.find(
      ({ name }) => name === newValue.account
    );
    const matchedManufacturer = manufacturerMappings.find(
      ({ name }) => name === patientManufacturer
    );

    const hasDeviceModelInformation =
      newValue.device &&
      (newValue.deviceInformation?.modelName ||
        newValue.deviceInformation?.modelNumber);

    let patientManufacturerOptions = [];
    if (matchedManufacturer && (hasDeviceModelInformation || isRpmDevice)) {
      patientManufacturerOptions.push({
        name: matchedManufacturer.name,
        rpmDevice: false,
      });
    } else {
      patientManufacturerOptions =
        currentUser?.manufacturer
          .filter((manufacturer) => manufacturers.includes(manufacturer))
          .map((manufacturer) => ({
            name: manufacturer,
            rpmDevice: false,
          })) || [];
    }

    if (newValue.rpm_devices) {
      const manufacturerOptions = RPM_DEVICE_OPTIONS.filter((option) =>
        Object.hasOwn(newValue.rpm_devices, option.key)
      ).map((option) => ({ name: option.value, rpmDevice: true }));

      const uniqueManufacturerOptions = manufacturerOptions.filter(
        (option) =>
          !patientManufacturerOptions.some(({ name }) => name === option.name)
      );

      patientManufacturerOptions.push(...uniqueManufacturerOptions);
    }

    if (isValidArray(patientManufacturerOptions)) {
      const filteredManufacturers = patientManufacturerOptions.filter(
        (option) => option.rpmDevice && option.name !== fields.manufacturer
      );
      dispatch(setPatientManufacturers(patientManufacturerOptions));
      dispatch(setPatientAdditionalManufacturers(filteredManufacturers));
    }

    const account = newValue.account || newValue.clinic;
    const isCrossFacilityUpload = isCrossFacilityClinicUpload(account);
    const hasAccountAccess =
      checkHasAccountAccess(account) || isCrossFacilityUpload;
    const hasManufacturerAccess =
      checkHasManufacturerAccess(patientManufacturer);
    const fieldsCopy = {
      ...fields,
      isRpmDevice,
      mrn: newValue.mrn || "",
      dob: newValue.dob || null,
      account: hasAccountAccess ? account : "",
      reportAccount: isCrossFacilityUpload
        ? filterNonUCHAccounts(currentUser?.accounts)?.[0]
        : "",
      lastName: newValue.lastName || "",
      evoke_id: newValue.evoke_id || "",
      modelName:
        newValue?.deviceInformation?.modelName || newValue?.modelName || "",
      firstName: newValue.firstName || "",
      rpmDevices: newValue.rpm_devices || {},
      modelNumber:
        newValue?.deviceInformation?.modelNumber || newValue?.modelNumber || "",
      deviceSerialNumber:
        newValue?.deviceInformation?.modelNumber || newValue?.modelNumber || "",
      report_date: newValue.report_date || null,
      manufacturer: hasManufacturerAccess ? patientManufacturer || "" : "",
      accountFolder:
        matchedAccount?.folder ||
        (isCrossFacilityUpload &&
          accessedAccountMappings?.find(
            ({ name }) =>
              name === filterNonUCHAccounts(currentUser?.accounts)?.[0]
          )?.folder) ||
        "",
      clinician_notes: newValue.clinician_notes || "",
      leadInformation: newValue.leadInformation || {},
      doi: formatDate(newValue.doi, "MM/DD/YYYY") || "",
      otherDeviceInformation: newValue.otherDeviceInformation || {},
      patient_serial_number: newValue.serial_number || "",
      name: `${newValue.lastName || ""} ${newValue.firstName || ""}`,
      device: isRpmDevice
        ? patientManufacturer
        : newValue?.device || newValue?.deviceInformation?.modelName || "",
      batteryInformation: newValue.deviceBatteryInformation || {},
      file: `${newValue.lastName || ""}, ${newValue.firstName || ""} ${
        newValue.report_date || ""
      } ${newValue?.device || newValue?.deviceInformation?.modelName || ""}`,

      manufacturerFolder: matchedManufacturer?.folder
        ? matchedManufacturer?.folder
        : !newValue.manufacturerFolder && isRpmDevice
          ? "rpm"
          : "",
      physicians: newValue?.information?.physicians,
      anticoag: newValue?.information?.anticoag_information?.anticoag || "",
      anticoagStatus:
        newValue?.information?.anticoag_information?.anticoag_status ||
        "unknown",
      refusesOAC:
        newValue?.information?.anticoag_information?.refuses_oac || false,
      laaoDevice: newValue?.information?.laao_device || false,
    };

    if (isTransferredFromRpm) {
      const reportBillingDate = rpmPatientBilling[0]?.billing_date || null;
      fieldsCopy.report_billing_date = reportBillingDate
        ? formatDate(reportBillingDate, "MM/DD/YYYY")
        : null;
    }
    setFields(fieldsCopy);
    validateFields(fieldsCopy);
    dispatch(setExistingPatientFieldValues(fieldsCopy));
  };

  const handleInOfficePatientDetailsSelect = (
    patientDetails,
    isExistingPatient
  ) => {
    dispatch(setIsExistingPatient(isExistingPatient));
    handleExistingPatientSelect(patientDetails);
  };

  const handleInOfficePatientRecordSelect = (
    { selectedPatientRecord },
    isExistingPatient
  ) => {
    handleInOfficePatientDetailsSelect(
      selectedPatientRecord,
      isExistingPatient
    );
    setShowSimilarPatientsFromInOfficeFile(false);
    dispatch(setSimilarPatientsFromInOfficeFile([]));
  };

  const handlePatientTypeSwitch = () => {
    resetForm();
    dispatch(setIsSubmitClicked(false));
    dispatch(setShowSwitchUserTypeModal(false));
    dispatch(setIsExistingPatient(!isExistingPatient));
    dispatch(setExistingPatientFieldValues(null));
  };

  const fetchManufacturerList = async () => {
    try {
      setFetchingManufacturers(true);
      if (manufacturerList.length > 0) {
        return;
      }
      const manufacturers = await fetchManufacturers();
      dispatch(setManufacturers(manufacturers));
    } catch (error) {
      showToast({ message: error.message, type: TOAST_SEVERITY.ERROR });
    } finally {
      setFetchingManufacturers(false);
    }
  };

  const handleAddNewDevice = (newDeviceInfo) => {
    const {
      doi,
      account,
      rpmDevices,
      physicians,
      isRpmDevice,
      manufacturer,
      accountFolder,
      manufacturerFolder,
      additionalManufacturer,
      deviceInformation: { modelName, modelNumber },
    } = newDeviceInfo;

    const newDevice = {
      account,
      rpmDevices,
      physicians,
      isRpmDevice,
      manufacturer,
      accountFolder,
      device: modelName,
      deviceSerialNumber: modelNumber,
      manufacturerFolder,
      additionalManufacturer,
      doi: getFormattedDate(doi, "MM/DD/YYYY"),
    };

    setFields((prevValues) => ({ ...prevValues, ...newDevice }));
    dispatch(setSelectedBillingCodes([]));
    dispatch(setShowAddNewDeviceModal(false));
    if (reportTemplates.length > 0) {
      handleReportTemplates({ ...fields, ...newDevice });
    }
  };

  const onAddInOfficeChecksClick = () => {
    dispatch(setIsInOfficeUpload(true));
  };

  const handleInOfficeUpload = async () => {
    try {
      dispatch(setIsUploadingInOfficeFile(true));
      const response = await uploadInOfficeFile(inOfficeFile);
      if (!response?.error) {
        dispatch(setInOfficeUploadPatientDetails(response?.data));
        dispatch(setShowInOfficeUploadPatientFoundDetails(true));
        dispatch(setIsInOfficeUpload(false));
        dispatch(setInOfficeFile(null));
        dispatch(setInOfficeFileName(""));
      }
    } catch (error) {
      showErrorToast(error.message);
    } finally {
      dispatch(setIsUploadingInOfficeFile(false));
    }
  };

  const resetInOfficeUpload = async () => {
    dispatch(setInOfficeUploadPatientDetails(null));
    dispatch(setShowInOfficeUploadPatientFoundDetails(false));
  };

  const handleUpdatedReportTemplateFetch = async () => {
    if (!patientInfoToUpdate) {
      return;
    }

    const { key, newValue } = patientInfoToUpdate;
    const isBillingCodeUpdate = key === "billingDiagnosisCodes";
    const isChangeDevice = key === "changeDevice";

    if (isChangeDevice) {
      dispatch(setShowAddNewDeviceModal(true));
    } else {
      if (isBillingCodeUpdate) {
        dispatch(setSelectedBillingCodes(Object.keys(newValue)));
      }

      onUpdate({
        [key]: isBillingCodeUpdate ? Object.values(newValue) : newValue,
      });
    }
    dispatch(setShowReportTemplateUpdateAlert(false));
  };

  const isInofficeClinicUpload = () => {
    return (
      checkIsCurrentUserOfClinic() &&
      transmissionType === "inOffice" &&
      isExistingPatient
    );
  };

  const isCrossFacilityClinicUpload = (account) => {
    return (
      isInofficeClinicUpload() && !currentUser?.accounts?.includes(account)
    );
  };

  return {
    fields,
    devices,
    currentUser,
    isClinicUser,
    isIDTFBilling,
    rpmReportUrl,
    isUCHPatient,
    isSubmitting,
    selectedPatient,
    showCustomNotes,
    reportTemplates,
    isInOfficeUpload,
    transmissionType,
    isExistingPatient,
    patientInfoToUpdate,
    isFetchingRpmPatient,
    patientManufacturers,
    showAddNewDeviceModal,
    fetchingManufacturers,
    showSwitchUserTypeModal,
    showCrossFacilityPatientWarningModal,
    selectedCrossFacilityPatient,
    setSelectedBillingCodes,
    isFetchingReportTemplates,
    inOfficeUploadPatientDetails,
    showReportTemplateUpdateAlert,
    similarPatientsFromInOfficeFile,
    showSimilarPatientsFromInOfficeFile,
    showInOfficeUploadPatientFoundDetails,
    inOfficeUploadPatientInformationForManualUpload,
    isError,
    dispatch,
    onUpdate,
    resetForm,
    setFields,
    saveUpload,
    onFilesSelected,
    pendingAssociatedNotes,
    pendingExtractionResults,
    showExtractButton,
    canExtractReportNotes,
    extractReportNotesTooltip,
    handleExtractReportPdfData,
    handleAddNewDevice,
    resetInOfficeUpload,
    handleInOfficeUpload,
    fetchManufacturerList,
    handlePatientTypeSwitch,
    onAddInOfficeChecksClick,
    handleExistingPatientSelect,
    handleUpdatedReportTemplateFetch,
    handleInOfficePatientRecordSelect,
    handleInOfficePatientDetailsSelect,
    setShowSimilarPatientsFromInOfficeFile,
    isInofficeClinicUpload,
    isCrossFacilityClinicUpload,
    setShowCrossFacilityPatientWarningModal,
    setSelectedCrossFacilityPatient,
    showMissingBillingCodeTemplateModal,
  };
};

export default useManualUpload;
